/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { AppConfig } from 'src/app/app.config';
import { ExtFilesDaoService } from '../ext-files-dao.service';
import { ExtFile } from 'src/app/_model/ext-file';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { ExtFileDbTableDefinition } from './ext-file-db-table-definition';
import { LocalExtFileDbTableDefinition } from './local-ext-file-db-table-definition';

@Injectable()
export class ExtFilesDbDaoService implements ExtFilesDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private appParameter: AppParameterService,
    private extFileTableDefinition: ExtFileDbTableDefinition,
    private localExtFileTableDefinition: LocalExtFileDbTableDefinition
  ) {}

  public async saveExtFiles(extFiles: ExtFile[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      var extFileQueries: Query[] = [];
      for (const extFile of extFiles) {
        let extFileQuerie: Query = {
          statement:
            'INSERT INTO ' +
            this.extFileTableDefinition.tableName +
            '(id, databag, fid, fileextension, synctype, fileidentifier, ' +
            ' filecontent, extfiletype, filesize, ownerid, ' +
            ' objref, version, extfileid, extfiletypeshortname, enableplanmarker, acid, aclabel, acukey,' +
            ' draftOwnerEmpId, draftOwnerMcId, draftOwnerServiceJobId, filledFormData, draftdynformid)' +
            ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)' +
            ' ON CONFLICT(id) DO UPDATE SET' +
            '   databag=excluded.databag,' +
            '   fid=excluded.fid,' +
            '   fileextension=excluded.fileextension,' +
            '   synctype=excluded.synctype,' +
            '   fileidentifier=excluded.fileidentifier,' +
            '   filecontent=excluded.filecontent,' +
            '   extfiletype=excluded.extfiletype,' +
            '   filesize=excluded.filesize,' +
            '   ownerid=excluded.ownerid,' +
            '   objref=excluded.objref,' +
            '   version=excluded.version,' +
            '   extfileid=excluded.extfileid,' +
            '   extfiletypeshortname=excluded.extfiletypeshortname,' +
            '   acid=excluded.acid,' +
            '   aclabel=excluded.aclabel,' +
            '   acukey=excluded.acukey,' +
            '   enableplanmarker=excluded.enableplanmarker,' +
            '   draftOwnerEmpId=excluded.draftOwnerEmpId,' +
            '   draftOwnerMcId=excluded.draftOwnerMcId,' +
            '   draftOwnerServiceJobId=excluded.draftOwnerServiceJobId,' +
            '   filledFormData=excluded.filledFormData,' +
            '   draftdynformid=excluded.draftdynformid;',
          parameters: [
            Number(extFile.getImsId()),
            extFile.databag ? JSON.stringify(extFile.databag) : undefined,
            extFile.fid,
            extFile.fileextension,
            extFile.synctype,
            extFile.fileidentifier,
            extFile.filecontent,
            extFile.extfiletypesyscode,
            extFile.filesize,
            extFile.ownerid,
            extFile.objref,
            extFile.version,
            extFile.extfileid,
            extFile.extfiletypeshortname,
            extFile.enableplanmarker,
            extFile.activityConfirmation
              ? Number(extFile.activityConfirmation.getImsId())
              : undefined,
            extFile.activityConfirmation
              ? extFile.activityConfirmation.label
              : undefined,
            extFile.acukey,
            extFile.legacyDraftOwnerEmpId,
            extFile.legacyDraftOwnerMcId,
            extFile.legacyDraftOwnerServiceJobId,
            extFile.legacyFilledFormData,
            extFile.legacyDraftdynformid,
          ],
        };
        extFileQueries.push(extFileQuerie);
      }
      await this.dbPersistenceService.executeBatch(db, extFileQueries);
    } catch (error) {
      this.logger.log(
        'Error saving ext files to db',
        error,
        '-',
        error?.message,
      );
    }
  }

  public async getExtFiles(): Promise<ExtFile[]> {
    let db = await this.dbPersistenceService.getImsDb();
    let extFiles: ExtFile[] = [];
    try {
      // Synced ExtFiles
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.extFileTableDefinition.tableName + ';',
        parameters: null,
      });

      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        extFiles.push(
          ExtFile.fromDto(
            {
              id: row['id'],
              secondaryid: undefined,
              fid: row['fid'],
              fileextension: row['fileextension'],
              synctype: row['synctype'],
              fileidentifier: row['fileidentifier'],
              // fileidentifier is mapped to dispname, so reading from db fileidentifier needs to be put to fileidentifier
              dispname: row['fileidentifier'],
              filecontent: row['filecontent'],
              extfiletypesyscode: row['extfiletype'],
              filesize: row['filesize'],
              ownerid: row['ownerid'],
              objref: row['objref'],
              version: row['version'],
              extfileid: row['extfileid'],
              extfiletypeshortname: row['extfiletypeshortname'],
              downloadStatus: row['downloadStatus'],
              downloadKey: row['downloadKey'],
              enableplanmarker: row['enableplanmarker'] === 1,
              activityconfirmation: {
                id: row['acid'],
                label: row['aclabel'],
              },
              acukey: row['acukey'],
              draftownerempid: row['draftOwnerEmpId'] ?? undefined,
              draftownermcid: row['draftOwnerMcId'] ?? undefined,
              draftownerservicejobid: row['draftOwnerServiceJobId'] ?? undefined,
              filledformdata: row['filledFormData'] ?? undefined,
              draftdynformid: row['draftdynformid'] ?? undefined,
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
            this.appParameter.getFormatLabelForObject('ims/ExtFile'),
          ),
        );
      }
    } catch (error) {
      this.logger.log('Error reading extfiles from db', error);
    }

    try {
      // Local ExtFiles
      let localData = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.localExtFileTableDefinition.tableName + ';',
        parameters: null,
      });
      if (localData) {
        for (let i = 0; i < localData.values.length; i++) {
          let row = localData.values[i];
          const extFile = ExtFile.fromDto(
              {
                id: undefined,
                secondaryid: row['id'],
                fid: row['fid'],
                fileextension: row['fileextension'],
                synctype: row['synctype'],
                fileidentifier: row['fileidentifier'],
                // fileidentifier is mapped to dispname, so reading from db fileidentifier needs to be put to fileidentifier
                dispname: row['fileidentifier'],
                filecontent: row['filecontent'],
                extfiletypesyscode: row['extfiletype'],
                filesize: row['filesize'],
                ownerid: row['ownerid'],
                objref: row['objref'],
                version: row['version'],
                extfileid: row['extfileid'],
                extfiletypeshortname: row['extfiletypeshortname'],
                downloadStatus: row['downloadStatus'],
                downloadKey: row['downloadKey'],
                enableplanmarker: row['enableplanmarker'] === 1,
                activityconfirmation: {
                  id: row['acid'],
                  label: row['aclabel'],
                },
                acukey: row['acukey'],
              },
              row['databag'] ? JSON.parse(row['databag']) : undefined,
              this.appParameter.getFormatLabelForObject('ims/ExtFile'),
            );
          extFiles.push(
            extFile
          );
        }
      }
    } catch (error) {
      this.logger.log('Error reading local extfiles from db', error);
    }
    return extFiles;
  }

  public async createExtFile(extFile: ExtFile): Promise<ExtFile> {
      this.logger.log(this.constructor.name, 'create extFile:', extFile);
      try {
        let db = await this.dbPersistenceService.getImsDb();
        let parameters = [
            extFile.databag ? JSON.stringify(extFile.databag) : undefined,
            extFile.fid,
            extFile.fileextension,
            extFile.synctype,
            extFile.fileidentifier,
            extFile.filecontent,
            extFile.extfiletypesyscode,
            extFile.filesize,
            extFile.ownerid,
            extFile.objref,
            extFile.version,
            extFile.extfileid,
            extFile.extfiletypeshortname,
            extFile.enableplanmarker,
            extFile.activityConfirmation
              ? Number(extFile.activityConfirmation.getImsId())
              : undefined,
            extFile.activityConfirmation
              ? extFile.activityConfirmation.label
              : undefined,
            extFile.acukey,
            extFile.downloadStatus
        ];
        let values =
          "(?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)";
        let fields =
          '(databag, fid, fileextension, synctype, fileidentifier, ' +
          ' filecontent, extfiletype, filesize, ownerid, ' +
          ' objref, version, extfileid, extfiletypeshortname, enableplanmarker, acid, aclabel, acukey, downloadStatus)';
        let table = this.localExtFileTableDefinition.tableName;
  
        let query: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            table +
            ' ' +
            fields +
            ' VALUES ' +
            values,
          parameters: parameters,
        };
        await this.dbPersistenceService.executeStatement(db, query);
  
        let res = await this.dbPersistenceService.executeQuery(db, {
          statement: `SELECT id FROM ${table} WHERE extfileid = ? ORDER BY id DESC LIMIT 1`,
          parameters: [extFile.extfileid],
        });
        if (res && res.values) {
          extFile.id.secondaryId = res.values.shift()?.id;
          this.logger.log(
            'Updated extFile secondary id',
            extFile.id.secondaryId
          );
        }
        return extFile;
      } catch (error) {
        this.logger.log(
          'Error saving extFile to db',
          error,
          '-',
          error?.message
        );
        return undefined;
      }
    }

  public async updateDownloadStatus(extFile: ExtFile): Promise<void> {
    let db = await this.dbPersistenceService.getImsDb();
    let extFileQuery: Query = {
      statement: `UPDATE ${this.extFileTableDefinition.tableName} SET downloadStatus = "${extFile.downloadStatus}", downloadKey = "${extFile.downloadKey}" WHERE id = ${extFile.id.imsId} ;`,
      parameters: [],
    };
    await this.dbPersistenceService.executeStatement(db, extFileQuery);
  }

  public async deleteExtFiles(extFiles: ExtFile[]): Promise<void> {
    if (extFiles == undefined || extFiles?.length == 0) {
      return;
    }

    const ids: number[] = extFiles.map((it) => it.id.imsId);

    try {
      let db = await this.dbPersistenceService.getImsDb();
      let query: Query = {
        statement:
          'DELETE FROM ' +
          this.extFileTableDefinition.tableName +
          ' WHERE id IN (' +
          ids.join(',') +
          ');',
        parameters: [],
      };
      await this.dbPersistenceService.executeStatement(db, query);
    } catch (error) {
      this.logger.log('Error deleting extfiles from db', error);
    }
  }

  public async deleteLocalExtFile(extFile: ExtFile): Promise<void> {
    let db = await this.dbPersistenceService.getImsDb();
    try {
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' +
          this.localExtFileTableDefinition.tableName +
          ' WHERE id = (?);',
        parameters: [extFile.id.secondaryId],
      });
    } catch (error) {
      this.logger.log('Error deleting local extFile', extFile.id.secondaryId, 'from db', error);
    }
  }

  public async reset() {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let query: Query = {
        statement: `UPDATE ${this.extFileTableDefinition.tableName} SET downloadStatus = "NotDownloaded", downloadKey = NULL ;`,
        parameters: [],
      };
      await this.dbPersistenceService.executeStatement(db, query);
      this.logger.log(
        this.constructor.name,
        'truncate table',
        this.localExtFileTableDefinition.tableName,
      );
      await this.dbPersistenceService.truncateTable(
        db,
        this.localExtFileTableDefinition.tableName,
      );
    } catch (error) {
      this.logger.log('Error deleting reseting from db', error);
    }
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.extFileTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.extFileTableDefinition.tableName,
    );
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.localExtFileTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localExtFileTableDefinition.tableName,
    );
  }
}
